import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';
import { 
  Menu, 
  X, 
  ChevronRight, 
  LogOut,
  User, 
  LayoutDashboard,
  Sparkles,
  MapPin,
  Navigation,
} from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle hash scrolling
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'admin': return '/admin/dashboard';
      case 'driver': return '/driver/dashboard';
      default: return '/client/dashboard';
    }
  };

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const isHomePage = location.pathname === '/';
  const isDriverDashboard = location.pathname.startsWith('/driver/');

  const navLinks = [
    { href: '/', label: t('nav_home'), isAnchor: false },
    { href: '/how-it-works', label: t('nav_how_it_works'), isAnchor: false },
    { href: 'services', label: t('nav_services'), isAnchor: true },
    { href: '/contact', label: t('nav_contact'), isAnchor: false },
  ];

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.08)] py-2' 
            : isHomePage 
              ? 'bg-transparent py-4' 
              : 'bg-white py-3 shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link 
              to="/" 
              className={`flex items-center gap-3 group ${
                isDriverDashboard ? '-ml-10 md:-ml-20 lg:-ml-24' : '-ml-6 md:-ml-14 lg:-ml-16'
              }`}
              data-testid="navbar-logo"
            >
              <div className="relative flex items-center justify-center">
                <div className={`absolute -inset-1.5 rounded-xl blur-sm opacity-0 group-hover:opacity-40 transition-opacity duration-300 ${
                  isScrolled || !isHomePage ? 'bg-orange-500' : 'bg-white'
                }`}></div>
                <div className={`relative flex items-center justify-center w-10 h-10 rounded-xl shadow-lg transform group-hover:scale-110 transition-all duration-300 ${
                  isScrolled || !isHomePage 
                    ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700/50' 
                    : 'bg-white/10 backdrop-blur-md border border-white/30'
                }`}>
                  <Navigation className={`w-5 h-5 transition-colors duration-300 ${
                    isScrolled || !isHomePage ? 'text-orange-500' : 'text-white'
                  }`} />
                  <MapPin className={`w-2.5 h-2.5 absolute top-2 right-2 transition-colors duration-300 ${
                    isScrolled || !isHomePage ? 'text-white' : 'text-orange-400'
                  }`} />
                </div>
              </div>
              <span className={`text-xl font-bold tracking-tight transition-colors duration-300 ${
                isScrolled || !isHomePage ? 'text-slate-900' : 'text-white'
              }`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                Convoyage<span className="text-orange-500 font-semibold">OS</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                link.isAnchor ? (
                  <button 
                    key={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      isScrolled || !isHomePage
                        ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' 
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link 
                    key={link.href}
                    to={link.href} 
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      isScrolled || !isHomePage
                        ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' 
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>

            {/* Desktop Auth Buttons + Language */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageSelector variant="navbar" />
              
              {user ? (
                <>
                  <Link to={getDashboardLink()}>
                    <Button 
                      className={`rounded-full px-5 gap-2 transition-all duration-300 ${
                        isScrolled || !isHomePage
                          ? 'bg-slate-900 hover:bg-slate-800 text-white'
                          : 'bg-white text-slate-900 hover:bg-white/90'
                      }`}
                      data-testid="nav-dashboard-btn"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      {t('nav_dashboard')}
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={handleLogout}
                    className={`rounded-full transition-all ${
                      isScrolled || !isHomePage
                        ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="nav-logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button 
                      variant="ghost" 
                      className={`rounded-full px-5 transition-all duration-300 ${
                        isScrolled || !isHomePage
                          ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                          : 'text-white hover:bg-white/10'
                      }`}
                      data-testid="nav-login-btn"
                    >
                      {t('nav_login')}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button 
                      className={`rounded-full px-5 gap-2 transition-all duration-300 shadow-lg hover:shadow-xl ${
                        isScrolled || !isHomePage
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'
                          : 'bg-white text-slate-900 hover:bg-white/90'
                      }`}
                      data-testid="nav-register-btn"
                    >
                      <Sparkles className="w-4 h-4" />
                      {t('nav_register')}
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              <LanguageSelector variant="navbar" />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-xl transition-all ${
                  isScrolled || !isHomePage
                    ? 'text-slate-900 hover:bg-slate-100'
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-2xl transition-all duration-300 overflow-hidden ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-4 space-y-2">
            {navLinks.map((link) => (
              link.isAnchor ? (
                <button 
                  key={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="flex items-center justify-between w-full p-3 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors text-left"
                >
                  <span className="font-medium">{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              ) : (
                <Link 
                  key={link.href}
                  to={link.href} 
                  className="flex items-center justify-between p-3 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <span className="font-medium">{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
              )
            ))}
            
            <div className="pt-4 border-t border-slate-100 space-y-2">
              {user ? (
                <>
                  <Link to={getDashboardLink()} className="block">
                    <Button className="w-full rounded-xl bg-slate-900 hover:bg-slate-800 gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      {t('nav_dashboard')}
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="w-full rounded-xl gap-2"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4" />
                    {t('nav_logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block">
                    <Button variant="outline" className="w-full rounded-xl">
                      {t('nav_login')}
                    </Button>
                  </Link>
                  <Link to="/register" className="block">
                    <Button className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 gap-2">
                      <Sparkles className="w-4 h-4" />
                      {t('nav_register')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      {/* Spacer for fixed navbar on non-home pages */}
      {!isHomePage && <div className="h-16" />}
    </>
  );
}
